import type { FormTreeQuestion } from '../../types';
import {
  buildQuestionTreeHierarchy,
  type QuestionTreeParentInfo,
  walkQuestionTree,
} from '../../walkQuestionTree';
import { resolveYesNoOptionPair } from '../primitives/yesNoRadio';
import { assertLayoutNodeExhaustive } from './layoutAssertNever';
import {
  tableSlotNodes,
  walkLayoutStructureChildren,
} from './layoutStructureChildLists';
import type { ConditionalWhenClause, LayoutNode } from './types';
import { resolveConditionalClauses } from './whenClause';

export type LayoutSchemaGateIssue =
  | {
      qid: string;
      kind: 'top-level-gated';
      gateType: 'conditional' | 'disabled-when';
    }
  | {
      qid: string;
      kind: 'non-ancestor-gate';
      gateQid: string;
      gateType: 'conditional' | 'disabled-when';
    }
  | {
      qid: string;
      kind: 'wrong-ancestor-answer';
      gateQid: string;
      gateAnswer: string;
      /** Sorted schema answer key(s) for this ancestor (one, or several when multi-homed). */
      expectedAnswer: string;
      gateType: 'conditional' | 'disabled-when';
      /** True when the clause was `{ qid, in: [...] }` (shapes the summary wording). */
      gateInList?: true;
    }
  | {
      qid: string;
      kind: 'multi-home-requires-in';
      gateQid: string;
      gateAnswer: string;
      /** Sorted schema answer keys the field is multi-homed under. */
      expectedAnswer: string;
      gateType: 'conditional' | 'disabled-when';
    }
  | {
      qid: string;
      kind: 'negated-branch-subitem';
      /** The schema parent whose answer should decide this field's fate. */
      parentQid: string;
      /** Schema subitems answer key(s) linking parent to field. */
      expectedAnswer: string;
      gateType: 'conditional';
    }
  | {
      qid: string;
      kind: 'ungated-subitem';
      /** The schema parent whose answer should decide this field's fate. */
      parentQid: string;
      /** Schema subitems answer key(s) linking parent to field. */
      expectedAnswer: string;
    };

type GateType = 'conditional' | 'disabled-when';

type GateFrame = {
  gateType: GateType;
  clauses: ConditionalWhenClause[];
  /**
   * True for a `conditional`'s `elseChildren`: that branch renders when the
   * clauses are false, so a clause here gates on the negation of what it says.
   */
  negated?: boolean;
};

/** ancestor QID → schema subitems answer key(s) linking that ancestor to the field. */
type AncestorAnswerPath = Map<string, string[]>;

/** Parsed `{ qid, eq | includes | in }` clause used for schema-path validation. */
type SchemaAncestorClause = {
  gateQid: string;
  issueKeySuffix: string;
  displayValue: string;
  isInList: boolean;
  matchesSchemaPath: (expectedAnswers: string[]) => boolean;
};

/** Stable display for one or many schema answer keys. */
export function formatExpectedAncestorAnswers(
  expectedAnswers: string[]
): string {
  if (expectedAnswers.length === 0) return '';
  if (expectedAnswers.length === 1) return expectedAnswers[0]!;
  return expectedAnswers.join(' | ');
}

function parseSchemaAncestorClause(
  clause: ConditionalWhenClause
): SchemaAncestorClause | null {
  if (!('qid' in clause)) return null;

  if ('eq' in clause) {
    return {
      gateQid: clause.qid,
      issueKeySuffix: clause.eq,
      displayValue: clause.eq,
      isInList: false,
      matchesSchemaPath: expectedAnswers => expectedAnswers.includes(clause.eq),
    };
  }

  if ('includes' in clause) {
    return {
      gateQid: clause.qid,
      issueKeySuffix: clause.includes,
      displayValue: clause.includes,
      isInList: false,
      matchesSchemaPath: expectedAnswers =>
        expectedAnswers.includes(clause.includes),
    };
  }

  if ('in' in clause) {
    const displayValue =
      clause.in.length === 0 ? '[]' : `[${clause.in.join(', ')}]`;
    return {
      gateQid: clause.qid,
      issueKeySuffix: `in:${[...clause.in].sort().join(',')}`,
      displayValue,
      isInList: true,
      // Must cover every schema home (one gate for multi-homed children).
      matchesSchemaPath: expectedAnswers =>
        expectedAnswers.every(key => clause.in.includes(key)),
    };
  }

  return null;
}

function buildAncestorAnswerPaths(
  parentOf: Map<string, QuestionTreeParentInfo>
): Map<string, AncestorAnswerPath> {
  const paths = new Map<string, AncestorAnswerPath>();

  const collect = (qid: string): AncestorAnswerPath => {
    const cached = paths.get(qid);
    if (cached) return cached;

    const path: AncestorAnswerPath = new Map();
    const parent = parentOf.get(qid);
    if (parent != null) {
      path.set(parent.parentQid, [...parent.answerKeys]);
      for (const [ancestorQid, answerKeys] of collect(parent.parentQid)) {
        path.set(ancestorQid, answerKeys);
      }
    }
    paths.set(qid, path);
    return path;
  };

  for (const qid of parentOf.keys()) {
    collect(qid);
  }
  return paths;
}

function validateSchemaGateClause(
  fieldQid: string,
  frame: GateFrame,
  clause: ConditionalWhenClause,
  ancestorPath: AncestorAnswerPath,
  seen: Set<string>
): LayoutSchemaGateIssue | null {
  const parsed = parseSchemaAncestorClause(clause);
  if (parsed == null) return null;

  const dedupeKey = `${frame.gateType}:${parsed.gateQid}:${parsed.issueKeySuffix}`;
  if (seen.has(dedupeKey)) return null;

  // Non-ancestor gating is wrong in either direction: an `elseChildren` branch
  // gated on an unrelated QID still hands that QID control of the field's
  // visibility, so this check runs for negated frames too.
  const expectedAnswers = ancestorPath.get(parsed.gateQid);
  if (expectedAnswers == null) {
    seen.add(dedupeKey);
    return {
      qid: fieldQid,
      kind: 'non-ancestor-gate',
      gateQid: parsed.gateQid,
      gateType: frame.gateType,
    };
  }

  // Past here the checks read the clause's literal value as the answer the
  // field renders on. An `elseChildren` clause holds on the answers it does
  // NOT name, so reporting it that way would name a value the author never
  // asked to gate on. `findParentGate` judges the branch's effective answer
  // instead, and reports `negated-branch-subitem` when it excludes the
  // schema's.
  if (frame.negated === true) return null;

  // Multi-homed under several answers of this parent: require one `in` gate
  // covering all homes — do not copy-paste the field under per-answer
  // `eq` / `includes` branches.
  if (expectedAnswers.length > 1 && !parsed.isInList) {
    seen.add(dedupeKey);
    return {
      qid: fieldQid,
      kind: 'multi-home-requires-in',
      gateQid: parsed.gateQid,
      gateAnswer: parsed.displayValue,
      expectedAnswer: formatExpectedAncestorAnswers(expectedAnswers),
      gateType: frame.gateType,
    };
  }

  if (parsed.matchesSchemaPath(expectedAnswers)) return null;

  seen.add(dedupeKey);
  return {
    qid: fieldQid,
    kind: 'wrong-ancestor-answer',
    gateQid: parsed.gateQid,
    gateAnswer: parsed.displayValue,
    expectedAnswer: formatExpectedAncestorAnswers(expectedAnswers),
    gateType: frame.gateType,
    ...(parsed.isInList ? { gateInList: true as const } : {}),
  };
}

const sameAnswer = (a: string, b: string): boolean =>
  a.trim() === b.trim() || a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The answer an `elseChildren` branch effectively renders on, when that is a
 * single schema answer.
 *
 * For a two-option Yes/No parent, `eq` is the one clause whose negation is
 * itself a single answer: `whenClauseSatisfied` treats an absent answer as
 * "No", so `eq: 'No'` holds while the question is blank and its else branch
 * renders on exactly "Yes". Everything else -- a 3+ option radio, `includes`,
 * `in` -- negates to a set of answers, not one, so it has no effective answer.
 *
 * The option match is exact, matching how `whenClauseSatisfied` compares a
 * *present* answer: with schema value "No", `eq: 'no'` never holds once the
 * question is answered, so its else branch renders on "Yes" AND "No" and has
 * no single effective answer. (Only the absent-answer fallback is lenient
 * about case, which is why a mis-cased gate still looks correct while blank.)
 */
function negatedBranchAnswer(
  clause: ConditionalWhenClause,
  parentValues: string[] | undefined
): string | null {
  if (!('qid' in clause) || !('eq' in clause)) return null;
  const pair = resolveYesNoOptionPair(parentValues);
  if (!pair) return null;
  if (clause.eq === pair.noValue) return pair.yesValue;
  if (clause.eq === pair.yesValue) return pair.noValue;
  return null;
}

/**
 * How the field's own schema parent is gated in this scope:
 * `null` when no clause names it, `'negated'` when only an `elseChildren`
 * branch does and that branch renders on answers the schema excludes,
 * `'positive'` otherwise.
 */
function findParentGate(
  gateStack: GateFrame[],
  parentQid: string,
  expectedAnswers: string[],
  valuesByQid: Map<string, string[] | undefined>
): 'positive' | 'negated' | null {
  let negated: 'negated' | null = null;
  for (const frame of gateStack) {
    for (const clause of frame.clauses) {
      if (parseSchemaAncestorClause(clause)?.gateQid !== parentQid) continue;
      if (frame.negated !== true) return 'positive';
      // An else branch is still a correct gate when its effective answer is
      // the schema's: `eq: 'No'` on a Yes/No parent renders "Yes" in else,
      // the same visibility a positive `eq: 'Yes'` gate would give.
      //
      // Only with a single clause, though: `conditionalWhenSatisfied` requires
      // every clause, so the else branch renders when ANY of them fails. Under
      // `whenAll: [parent = No, other = X]` the branch still shows while
      // `parent = No` and `other` is not X, so no one clause's inverse
      // describes when it renders.
      const effective =
        frame.clauses.length === 1
          ? negatedBranchAnswer(clause, valuesByQid.get(parentQid))
          : null;
      if (
        effective != null &&
        expectedAnswers.some(key => sameAnswer(key, effective))
      ) {
        return 'positive';
      }
      negated = 'negated';
    }
  }
  return negated;
}

function recordFieldQid(
  qid: string,
  gateStack: GateFrame[],
  hierarchy: ReturnType<typeof buildQuestionTreeHierarchy>,
  ancestorPaths: Map<string, AncestorAnswerPath>,
  valuesByQid: Map<string, string[] | undefined>,
  ungatedSeen: Set<string>,
  issues: LayoutSchemaGateIssue[]
) {
  if (hierarchy.topLevelQids.has(qid)) {
    if (gateStack.length === 0) return;
    const innermostGateType = gateStack[gateStack.length - 1]!.gateType;
    issues.push({ qid, kind: 'top-level-gated', gateType: innermostGateType });
    return;
  }

  const ancestorPath = ancestorPaths.get(qid);
  if (ancestorPath == null) return;

  // A subitem the schema hides behind a parent answer must say so in the
  // layout: the bespoke renderer has no `subitems` fallback, so an ungated
  // field is one that shows when the schema says it should not exist. Only
  // the field's own parent counts -- a gate on a grandparent alone still
  // renders the field while the parent is blank or answered otherwise.
  const parent = hierarchy.parentOf.get(qid);
  if (parent != null && !ungatedSeen.has(qid)) {
    const parentAnswerKeys = [...parent.answerKeys];
    const parentGate = findParentGate(
      gateStack,
      parent.parentQid,
      parentAnswerKeys,
      valuesByQid
    );
    if (parentGate !== 'positive') {
      ungatedSeen.add(qid);
      const expectedAnswer = formatExpectedAncestorAnswers(parentAnswerKeys);
      issues.push(
        parentGate === 'negated'
          ? {
              qid,
              kind: 'negated-branch-subitem',
              parentQid: parent.parentQid,
              expectedAnswer,
              gateType: 'conditional',
            }
          : {
              qid,
              kind: 'ungated-subitem',
              parentQid: parent.parentQid,
              expectedAnswer,
            }
      );
    }
  }

  if (gateStack.length === 0) return;

  const seen = new Set<string>();
  for (const frame of gateStack) {
    for (const clause of frame.clauses) {
      const issue = validateSchemaGateClause(
        qid,
        frame,
        clause,
        ancestorPath,
        seen
      );
      if (issue != null) issues.push(issue);
    }
  }
}

function issueSortKey(issue: LayoutSchemaGateIssue): string {
  switch (issue.kind) {
    case 'top-level-gated':
      return `0:${issue.qid}`;
    case 'ungated-subitem':
      return `0:${issue.qid}:${issue.parentQid}`;
    case 'negated-branch-subitem':
      return `0:${issue.qid}:${issue.parentQid}:else`;
    case 'non-ancestor-gate':
      return `1:${issue.qid}:${issue.gateQid}:${issue.gateType}`;
    case 'multi-home-requires-in':
      return `2:${issue.qid}:${issue.gateQid}:${issue.gateAnswer}:${issue.gateType}`;
    case 'wrong-ancestor-answer':
      return `3:${issue.qid}:${issue.gateQid}:${issue.gateAnswer}:${issue.gateType}`;
  }
}

/**
 * Flags layout fields that violate schema parent/child gating rules:
 * - top-level schema QIDs must not sit inside `conditional` or `disabled-when`
 * - nested subitem QIDs inside a gate may reference schema ancestors on
 *   `{ qid, eq }` / `{ qid, includes }` / `{ qid, in }` clauses, but never
 *   non-ancestors
 * - single-home: `{ qid, eq }` / `{ qid, includes }` must use that ancestor's
 *   schema subitems answer key; `{ qid, in }` must include it
 * - multi-home (same child under several keys of one parent): do not gate with
 *   per-answer `eq`/`includes` (no copy-pasted branches); use one `{ qid, in }`
 *   that lists every schema home
 * - nested subitem QIDs must be gated on their own schema parent; rendering one
 *   ungated shows it when the schema says the parent's answer excludes it
 * - a subitem in a `conditional`'s `elseChildren` is gated on the negation of
 *   that gate; that is correct only when the negation is itself the schema's
 *   answer (a Yes/No parent gated `eq` on the opposite value)
 */
export function collectLayoutSchemaGateIssues(
  nodes: LayoutNode[],
  questions: FormTreeQuestion[]
): LayoutSchemaGateIssue[] {
  const hierarchy = buildQuestionTreeHierarchy(questions);
  const ancestorPaths = buildAncestorAnswerPaths(hierarchy.parentOf);
  // Needed to tell whether a parent is a Yes/No pair, whose `eq` gate negates
  // to a single answer -- see `negatedBranchAnswer`.
  const valuesByQid = new Map<string, string[] | undefined>();
  walkQuestionTree(questions, question => {
    valuesByQid.set(question.qid, question.values);
  });
  const issues: LayoutSchemaGateIssue[] = [];
  // A QID rendered in several places is reported once, matching how the
  // layout's other collectors treat repeats.
  const ungatedSeen = new Set<string>();

  const walk = (arr: LayoutNode[], gateStack: GateFrame[]) => {
    for (const node of arr) {
      switch (node.type) {
        case 'conditional': {
          const frame: GateFrame = {
            gateType: 'conditional',
            clauses: resolveConditionalClauses(node),
          };
          walk(node.children, [...gateStack, frame]);
          if (node.elseChildren) {
            walk(node.elseChildren, [
              ...gateStack,
              { ...frame, negated: true },
            ]);
          }
          break;
        }
        case 'disabled-when': {
          const frame: GateFrame = {
            gateType: 'disabled-when',
            clauses: node.whenAll,
          };
          walk(node.children, [...gateStack, frame]);
          break;
        }
        case 'table': {
          // Headers sit outside every row, so they carry only the inherited
          // gate stack.
          walk(tableSlotNodes(node.rowHeader), gateStack);
          for (const header of node.colHeaders ?? []) {
            walk(tableSlotNodes(header), gateStack);
          }
          // A row gates visibility with the same clause shape as `conditional`,
          // so push a per-row frame (when the row is actually gated) before
          // walking its label and cells. Ungated rows keep the inherited gate
          // stack.
          for (const row of node.rows) {
            const clauses = resolveConditionalClauses(row);
            const nextStack =
              clauses.length > 0
                ? [...gateStack, { gateType: 'conditional' as const, clauses }]
                : gateStack;
            walk(tableSlotNodes(row.label), nextStack);
            walk(row.cells, nextStack);
          }
          break;
        }
        case 'OASISRadio':
        case 'OASISYesNoCheckbox':
        case 'OASISSelect':
        case 'OASISText':
        case 'OASISPhysician':
        case 'OASISDate':
        case 'OASISTime':
        case 'OASISNumber':
        case 'radio-option':
          recordFieldQid(
            node.qid,
            gateStack,
            hierarchy,
            ancestorPaths,
            valuesByQid,
            ungatedSeen,
            issues
          );
          break;
        case 'OASISMultiselect':
          recordFieldQid(
            node.qid,
            gateStack,
            hierarchy,
            ancestorPaths,
            valuesByQid,
            ungatedSeen,
            issues
          );
          walkLayoutStructureChildren(node, childList =>
            walk(childList, gateStack)
          );
          break;
        case 'card':
        case 'stack':
        case 'grid':
        case 'flex':
        case 'box':
        case 'indent':
        case 'field-row':
        case 'divider':
        case 'static':
        case 'text':
        case 'display':
        case 'external-answer':
        case 'section-label':
        case 'sum':
        case 'repeat':
        case 'OASISMultiselectOption':
          // `external-answer`'s `ref` names a QID on another form, so it cannot
          // be checked against `questions` here -- and nothing else checks it
          // either. A stale ref renders as the node's `empty` text. `source` is
          // constrained by its union type in layout/types.ts; `ref` is not.
          walkLayoutStructureChildren(node, childList =>
            walk(childList, gateStack)
          );
          break;
        default:
          assertLayoutNodeExhaustive(node);
      }
    }
  };

  walk(nodes, []);
  return issues.sort((a, b) => issueSortKey(a).localeCompare(issueSortKey(b)));
}

function formatWrongAncestorAnswerIssue(
  issue: Extract<LayoutSchemaGateIssue, { kind: 'wrong-ancestor-answer' }>
): string {
  const isInList = issue.gateInList === true;
  const multi = issue.expectedAnswer.includes(' | ');
  const expectedPhrase = multi
    ? `one of ${issue.gateQid} = ${issue.expectedAnswer}`
    : `${issue.gateQid} = "${issue.expectedAnswer}"`;
  if (issue.gateType === 'disabled-when') {
    if (isInList) {
      return `${issue.qid} (disabled-when whenAll must keep field enabled on ${expectedPhrase}, but in clause ${issue.gateAnswer} does not include every home)`;
    }
    return `${issue.qid} (disabled-when whenAll must keep field enabled on ${expectedPhrase}, not "${issue.gateAnswer}")`;
  }
  if (isInList) {
    return `${issue.qid} (conditional when must match ${expectedPhrase}, but in clause ${issue.gateAnswer} does not include every home)`;
  }
  return `${issue.qid} (conditional when must match ${expectedPhrase}, not "${issue.gateAnswer}")`;
}

export function formatLayoutSchemaGateIssueSummary(
  issue: LayoutSchemaGateIssue
): string {
  switch (issue.kind) {
    case 'top-level-gated':
      return `${issue.qid} (top-level schema question inside ${issue.gateType})`;
    case 'ungated-subitem':
      return `${issue.qid} (schema subitem of ${issue.parentQid} rendered ungated; wrap it in a conditional or disabled-when on ${issue.parentQid} = ${issue.expectedAnswer})`;
    case 'negated-branch-subitem':
      return `${issue.qid} (schema subitem of ${issue.parentQid} rendered in a conditional's elseChildren, which shows when the gate is false; move it into children of a conditional on ${issue.parentQid} = ${issue.expectedAnswer})`;
    case 'non-ancestor-gate':
      return `${issue.qid} (gate references non-ancestor ${issue.gateQid} inside ${issue.gateType})`;
    case 'multi-home-requires-in':
      return `${issue.qid} (multi-homed under ${issue.gateQid} = ${issue.expectedAnswer}; use one {in:[...]} covering all homes, not per-answer "${issue.gateAnswer}")`;
    case 'wrong-ancestor-answer':
      return formatWrongAncestorAnswerIssue(issue);
  }
}
